// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------


// React
import { useState, useEffect } from 'react';

// Sentry
import * as Sentry from "@sentry/react";



// ----------------------------------------------------------------------------
//   Tracked Error Objects
// ----------------------------------------------------------------------------


/**
 * Error state tokens / "codes".
 */
export const ErrorState = Object.freeze({

  /** User data could not be retrieved from the API. */
  NO_USER_DATA:      'NO_USER_DATA',

  /** Video data could not be retrieved from the API. */
  NO_VIDEO_DATA:     'NO_VIDEO_DATA',

  /** Device webcam could not be initialized. */
  NO_WEBCAM:         'NO_WEBCAM',

  /** List of media devices could not be loaded from the user's device. */
  NO_MEDIA_DEVICES:  'NO_MEDIA_DEVICES',

  /** The user's video recording failed to complete. */
  RECORDING_FAILED:  'RECORDING_FAILED',

  /** User recording could not be uploaded. */
  UPLOAD_FAILED:     'UPLOAD_FAILED',
  NO_PRACTICE_AUDIO: 'NO_PRACTICE_AUDIO',
  ACTIVITY_LOOKUP_FAILED: 'ACTIVITY_LOOKUP_FAILED',

  /** Local recordings could not be loaded. */
  GET_STORED_RECORDINGS_FAILED: 'GET_STORED_RECORDINGS_FAILED',
});


/**
 * Custom error class indicating that the site is in a given error state.
 * By default, tracks this state (incl. the causing error) with Sentry.
 *
 * This error class focuses on application state (e.g. "can't use webcam")
 * as opposed to individual causes (e.g. "no devices found", "user won't
 * grant permissions", etc).
 * Multiple types of errors may be aggregated under one functional "state",
 * which directs what the application's fallback behavior will be.
 *
 * The cause is tracked on the linked `cause` error for debugging purposes.
 */
export class StateError extends Error {
  constructor( state, cause, options = { track: true } ) {

    // Fallback & unpacking for `options` object
    options = options ?? { track: true };
    const { track, ...superOptions } = options;

    // If causing error has a message, include it in this object's message
    const message = cause?.message ? `${state}: ${cause.message}` : state;

    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super( message, { ...superOptions, cause });

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, StateError);
    }

    // Fields
    this.name  = "StateError";
    this.state = state;
    this.track = track;

    // If desired, track this error with Sentry
    if (track) {
      Sentry.captureException( this, { tags: { state } } );
    }
  }
}



// ----------------------------------------------------------------------------
//   Error Tracking
// ----------------------------------------------------------------------------


export function useTrackedError() {

  // Create new React state to track error
  const [ errorObject, setErrorObject ] = useState({ state: null, object: null, message: [] });


  // When error object is updated, track it with Sentry
  useEffect(() => {
    if ( errorObject?.state ) {
      trackError( errorObject.state, errorObject.object );
    }
  }, [ errorObject ]);


  // Set a new error state
  function set( errorState, errorObject, errorMessage = [] ) {
    setErrorObject({
      state:   errorState,
      object:  errorObject,
      message: errorMessage,
    })
  }


  // Clear the existing error state
  function clear() {
    setErrorObject({ state: null, object: null, message: [] });
  }


  // Return the React state object and the two modification functions
  return [ errorObject, { set, clear } ]
}


/**
 * Track an error state with Sentry.
 */
export function trackError(errorState, errorObject) {

  // Wrap the custom ErrorState and source error into a single JS Error object
  const wrappedError = new Error( errorState, {cause: errorObject} )

  // Track with Sentry
  Sentry.captureException(wrappedError);

  // Return the custom error object
  return wrappedError;
}



// ----------------------------------------------------------------------------
//   Specific Error Classes
// ----------------------------------------------------------------------------


/**
 * Custom error class indicating that the current user is not logged in.
 * Untracked -- should simply be handled client side with a login page redirect.
 */
export class NotLoggedInError extends Error {}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------


export default {
  ErrorState,
  StateError,
  useTrackedError,
  trackError,
  NotLoggedInError,
}
